const { STATUS, CHECK_ID } = require('../constants');

// Module-level CSP violation buffer. Per design §4.2 the listener must be
// registered *around checkout render*: a frame-src violation fires when the
// browser loads the checkout iframe, which happens during render — before the
// diagnostics engine runs. armCspCollection() is therefore called from init
// BEFORE renderHtml() so that violation is captured; a listener attached only at
// check-run time (post-render) would miss it and falsely report `pass`.
let armed = false;
const buffer = [];

function armCspCollection(doc = (typeof document !== 'undefined' ? document : null)) {
  if (armed || !doc) {
    return;
  }
  armed = true;
  doc.addEventListener('securitypolicyviolation', (e) => {
    buffer.push({ violatedDirective: e.violatedDirective, blockedURI: e.blockedURI });
  });
}

function collectCspViolations(timeoutMs = 1500, doc = (typeof document !== 'undefined' ? document : null)) {
  // Fast path: collection was armed before render, so any violation is already
  // buffered — resolve immediately (no mandatory 1.5s wait).
  if (armed) {
    return Promise.resolve(buffer.slice());
  }
  // Fallback (unarmed): e.g. a standalone Checkout.runDiagnostics() console
  // re-run after render, where nothing armed early. Open a live window; note it
  // cannot catch a violation that already fired before this point.
  return new Promise((resolve) => {
    if (!doc) {
      resolve([]);
      return;
    }
    const violations = [];
    const handler = (e) => violations.push({ violatedDirective: e.violatedDirective, blockedURI: e.blockedURI });
    doc.addEventListener('securitypolicyviolation', handler);
    setTimeout(() => {
      doc.removeEventListener('securitypolicyviolation', handler);
      resolve(violations);
    }, timeoutMs);
  });
}

async function runCspCheck(ctx, apiConfig, collect = collectCspViolations) {
  if (!apiConfig) {
    return { id: CHECK_ID.CSP, status: STATUS.INFO, detail: 'diagnostics API unavailable — check skipped', evidence: { observedOutcome: 'unavailable' } };
  }
  const web = apiConfig.web || {};
  const expectedFrameSrc = web.allowed_iframe_domains || [];
  const violations = await collect();

  if (violations.length) {
    const v = violations[0];
    return {
      id: CHECK_ID.CSP,
      status: STATUS.WARN,
      detail: 'a Content-Security-Policy violation was observed',
      evidence: { expectedFrameSrc, violatedDirective: v.violatedDirective, blockedURI: v.blockedURI, observedOutcome: 'violation' },
    };
  }
  return {
    id: CHECK_ID.CSP,
    status: STATUS.PASS,
    detail: 'no CSP violation observed (config-expected, not header-verified)',
    evidence: { expectedFrameSrc, observedOutcome: 'ok' },
  };
}

module.exports = { runCspCheck, collectCspViolations, armCspCollection };
